import React, { useState, useMemo, useCallback } from 'react'
import { ChainStepEditor, PluginBadge } from './ChainStepEditor'

/**
 * Main visual editing interface for chain steps.
 *
 * Contains three sub-sections:
 *   1. ChainFlowDiagram — horizontal chain visualization
 *   2. SimulationPicker — add simulations to the chain
 *   3. Step list — renders ChainStepEditor for each step
 *
 * Props:
 *   steps[]        — current chain steps array
 *   simulations[]  — available simulations [{ pk, title, plugin, guid }]
 *   coursenum       — for building edit links
 *   onChange(newSteps)
 */

const PLUGIN_COLORS = {
  'spreadsheet':      { bg: '#e3f2fd', text: '#1565c0', label: 'Spreadsheet' },
  'statement-table':  { bg: '#e8f5e9', text: '#2e7d32', label: 'Statement Table' },
  'interactive-path': { bg: '#fff3e0', text: '#e65100', label: 'Interactive Path' },
  'intro-card':       { bg: '#f3e5f5', text: '#6a1b9a', label: 'Intro Card' },
  'sorting':          { bg: '#fce4ec', text: '#c62828', label: 'Sorting' },
}

// Compute accumulated variable names from steps 0..upToIndex-1
const getAccumulatedVarNames = (steps, upToIndex) => {
  const names = new Set()
  for (let i = 0; i < upToIndex; i++) {
    ;(steps[i].outputMap || []).forEach((entry) => {
      if (entry.name) names.add(entry.name)
    })
  }
  return Array.from(names).sort()
}

// ── Chain Flow Diagram ─────────────────────────────────────────────────────

const ChainFlowDiagram = ({ steps, simulations, selectedIndex, onSelectStep }) => {
  const simMap = useMemo(() => {
    const map = {}
    simulations.forEach((s) => { map[s.guid] = s })
    return map
  }, [simulations])

  if (steps.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: '#6c757d',
        fontStyle: 'italic',
        fontSize: '13px',
        border: '2px dashed #dee2e6',
        borderRadius: '8px',
      }}>
        No steps in the chain. Use the picker below to add simulations.
      </div>
    )
  }

  return (
    <div style={{
      overflowX: 'auto',
      padding: '12px 4px',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      background: '#fafbfc',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        minWidth: 'min-content',
      }}>
        {steps.map((step, i) => {
          const sim = simMap[step.guid]
          const plugin = sim ? sim.plugin : null
          const colors = PLUGIN_COLORS[plugin] || { bg: '#e9ecef', text: '#495057', label: plugin || '?' }
          const isSelected = i === selectedIndex
          const title = sim ? sim.title : step.guid

          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#adb5bd',
                  fontSize: '18px',
                  padding: '0 2px',
                  flexShrink: 0,
                }}>
                  &rarr;
                </div>
              )}
              <div
                onClick={() => onSelectStep(i)}
                style={{
                  border: isSelected ? '2px solid #4285f4' : '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  background: '#fff',
                  cursor: 'pointer',
                  minWidth: '120px',
                  maxWidth: '180px',
                  flexShrink: 0,
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxShadow: isSelected ? '0 0 0 2px rgba(66,133,244,0.2)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = '#adb5bd'
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = '#dee2e6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{
                    fontWeight: 700,
                    fontSize: '11px',
                    color: '#fff',
                    background: '#495057',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {i + 1}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {title}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: colors.text,
                    background: colors.bg,
                    borderRadius: '3px',
                    padding: '1px 5px',
                  }}>
                    {colors.label}
                  </span>
                  {!sim && (
                    <span style={{ color: '#dc3545', fontSize: '12px' }} title="GUID not found">&#9888;</span>
                  )}
                </div>
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

// ── Simulation Picker ──────────────────────────────────────────────────────

const SimulationPicker = ({ simulations, onAdd }) => {
  const [pluginFilter, setPluginFilter] = useState('')
  const [searchText, setSearchText] = useState('')

  const uniquePlugins = useMemo(() => {
    const set = new Set()
    simulations.forEach((s) => { if (s.plugin) set.add(s.plugin) })
    return Array.from(set).sort()
  }, [simulations])

  const filtered = useMemo(() => {
    let result = simulations
    if (pluginFilter) {
      result = result.filter((s) => s.plugin === pluginFilter)
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      result = result.filter((s) => (s.title || '').toLowerCase().includes(q))
    }
    return result
  }, [simulations, pluginFilter, searchText])

  return (
    <div style={{
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      background: '#fff',
    }}>
      <div style={{
        padding: '8px 12px',
        background: '#f4f5f7',
        borderBottom: '1px solid #dee2e6',
        fontWeight: 600,
        fontSize: '13px',
      }}>
        Add Simulation to Chain
      </div>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={pluginFilter}
            onChange={(e) => setPluginFilter(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '13px',
              width: '170px',
            }}
          >
            <option value="">All Plugins</option>
            {uniquePlugins.map((p) => {
              const info = PLUGIN_COLORS[p] || { label: p }
              return <option key={p} value={p}>{info.label}</option>
            })}
          </select>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by title..."
            style={{
              padding: '4px 8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '13px',
              flex: 1,
            }}
          />
        </div>
      </div>
      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
        {filtered.length > 0 ? (
          filtered.map((sim) => (
            <div
              key={sim.pk}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 12px',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '13px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sim.title}
                </span>
                <PluginBadge plugin={sim.plugin} />
                <code style={{ fontSize: '11px', color: '#6c757d', flexShrink: 0 }}>{sim.guid}</code>
              </div>
              <button
                type="button"
                onClick={() => onAdd(sim)}
                className="btn btn-myed btn-primary btn-primary-myed btn-sm"
                style={{ marginLeft: '8px', flexShrink: 0, padding: '2px 10px', fontSize: '12px' }}
              >
                + Add
              </button>
            </div>
          ))
        ) : (
          <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d', fontStyle: 'italic', fontSize: '13px' }}>
            {simulations.length === 0
              ? 'No simulations available in this course.'
              : 'No simulations match the current filter.'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Visual Editor ─────────────────────────────────────────────────────

export const ChainVisualEditor = ({ steps, simulations, coursenum, onChange }) => {
  const [selectedStepIndex, setSelectedStepIndex] = useState(null)

  // Build simulation lookup by GUID
  const simMap = useMemo(() => {
    const map = {}
    simulations.forEach((s) => { map[s.guid] = s })
    return map
  }, [simulations])

  const handleAddSimulation = useCallback((sim) => {
    const newStep = { guid: sim.guid, statePolicy: 'consume' }
    onChange([...steps, newStep])
  }, [steps, onChange])

  const handleUpdateStep = useCallback((index, newStep) => {
    const updated = steps.map((s, i) => (i === index ? newStep : s))
    onChange(updated)
  }, [steps, onChange])

  const handleRemoveStep = useCallback((index) => {
    onChange(steps.filter((_, i) => i !== index))
    if (selectedStepIndex === index) setSelectedStepIndex(null)
    else if (selectedStepIndex > index) setSelectedStepIndex(selectedStepIndex - 1)
  }, [steps, onChange, selectedStepIndex])

  const handleMoveStep = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= steps.length) return
    const updated = [...steps]
    const [moved] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, moved)
    onChange(updated)
    setSelectedStepIndex(toIndex)
  }, [steps, onChange])

  const handleSelectStep = useCallback((index) => {
    setSelectedStepIndex(index)
    // Scroll to the step editor panel
    setTimeout(() => {
      const el = document.querySelector(`[data-chain-step-index="${index}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 50)
  }, [])

  return (
    <div>
      {/* Flow Diagram */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
          Chain Flow
        </label>
        <ChainFlowDiagram
          steps={steps}
          simulations={simulations}
          selectedIndex={selectedStepIndex}
          onSelectStep={handleSelectStep}
        />
      </div>

      {/* Simulation Picker */}
      <div style={{ marginBottom: '16px' }}>
        <SimulationPicker
          simulations={simulations}
          onAdd={handleAddSimulation}
        />
      </div>

      <hr style={{ margin: '16px 0', borderColor: '#dee2e6' }} />

      {/* Step List */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
          <label style={{ fontWeight: 600, fontSize: '14px' }}>
            Steps ({steps.length})
          </label>
          {steps.length > 0 && (() => {
            let totalPoints = 0
            let totalTasks = 0
            steps.forEach((step) => {
              const sim = simMap[step.guid]
              if (sim && sim.tasks) {
                sim.tasks.forEach((t) => { totalPoints += (t.points || 0) })
                totalTasks += sim.tasks.length
              }
            })
            return (
              <span style={{ fontSize: '13px', color: '#495057' }}>
                <strong>Total:</strong> {totalPoints} points across {totalTasks} task{totalTasks !== 1 ? 's' : ''}
              </span>
            )
          })()}
        </div>

        {steps.map((step, index) => (
          <ChainStepEditor
            key={index}
            step={step}
            index={index}
            simulation={simMap[step.guid] || null}
            coursenum={coursenum}
            accumulatedVarNames={getAccumulatedVarNames(steps, index)}
            onUpdate={handleUpdateStep}
            onRemove={handleRemoveStep}
            onMoveUp={() => handleMoveStep(index, index - 1)}
            onMoveDown={() => handleMoveStep(index, index + 1)}
            isFirst={index === 0}
            isLast={index === steps.length - 1}
          />
        ))}

        {steps.length === 0 && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: '#6c757d',
            fontStyle: 'italic',
            border: '2px dashed #dee2e6',
            borderRadius: '8px',
          }}>
            No steps yet. Use the picker above to add simulations to the chain.
          </div>
        )}
      </div>
    </div>
  )
}
